import { useEffect } from 'react'
import { useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Card, CardBody } from '../components/ui/Card'
import { PageScrollArea, PageView } from '../components/ui/PageScrollArea'
import { LinkButton } from '../components/ui/Button'
import { DEFAULT_PRODUCT, PAGES, pagePath, type PageSlug } from '../lib/routes'
import { resetDocumentTitle } from '../i18n'

/** The in-page "not found" state for every invalid URL (MS-818, OQ-5 —
 * decided by the PO 2026-10-07): an unknown tab on a tabbed page
 * (`/usage/foo`), a deeper unknown path under one (`/usage/mambu-mcp/x`), a
 * tab segment on a tab-less page (`/monthly-limits/x`) and an unknown page
 * (`/foo`). Rendered in AppShell's content area, so the nav, header and theme
 * toggle stay, and with no redirect: the address stays as typed.
 *
 * The prototype has no error state, so this is new UI with no prototype
 * source — a recorded deviation (ARCHITECTURE_DECISIONS.md §40), built only
 * from existing primitives (Card, Button) and theme tokens so it works in
 * both themes without any new colour. No product tabs render here.
 *
 * `page` names the known page the bad path sits under, if any; the one action
 * goes to that page's default tab (tabbed), its fixed tab (Settings › Access,
 * MS-997), the bare page (tab-less), or Home. */
export function NotFound({ page }: Readonly<{ page?: PageSlug }>) {
  const { t } = useTranslation()
  const { pathname } = useLocation()

  const title = t('notFound.title')
  useEffect(() => {
    document.title = t('notFound.documentTitle', { title, brand: t('appShell.brand') })
    // Back to the brand in the language active *now*, not the title this page
    // found on arrival.
    return resetDocumentTitle
  }, [title, t])

  let target = '/'
  let action = t('notFound.goHome')
  if (page) {
    const def = PAGES[page]
    const label = t(def.labelKey)
    if (def.fixedTab) {
      target = pagePath(page)
      action = t('notFound.goToPageTab', { page: label, product: t(def.fixedTab.labelKey) })
    } else if (def.tabbed) {
      target = pagePath(page, DEFAULT_PRODUCT)
      // The product name is a literal, never translated (ADR §8); the
      // separator and word order belong to the translation.
      action = t('notFound.goToPageTab', { page: label, product: DEFAULT_PRODUCT })
    } else {
      target = pagePath(page)
      action = t('notFound.goToPage', { page: label })
    }
  }

  return (
    <PageView>
      <PageScrollArea>
        <Card>
          <CardBody className="flex flex-col items-start gap-2">
            <h1 className="text-[20px] font-semibold text-[var(--text-primary)]">{title}</h1>
            <p className="text-[13px] text-[var(--text-secondary)] mb-2 break-all">
              {t('notFound.body', { path: pathname })}
            </p>
            <LinkButton to={target}>{action}</LinkButton>
          </CardBody>
        </Card>
      </PageScrollArea>
    </PageView>
  )
}
